import { Controller } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { useForm } from '@refinedev/react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { toTitleCase, cn } from '@/lib/utils'
import { resolveFkMapping, FK_DEPENDENT_FILTERS } from '@/resources/fkMappings'
import { FkSelect } from './FkSelect'
import { MultiSelectInput } from './MultiSelectInput'
import {
  resolveFieldKind,
  ENUM_FIELDS,
  ARRAY_ENUM_FIELDS,
  COUNTRIES,
  HIDDEN_FIELDS,
  SALT_FIELDS,
} from '@/resources/fieldSchema'
import { Eye, EyeOff } from 'lucide-react'
import type { BaseRecord } from '@refinedev/core'

interface ResourceFormProps {
  resource: string
  action: 'create' | 'edit'
  defaultValues?: BaseRecord
  recordId?: string
}

function toDatetimeLocalValue(isoString: string): string {
  const d = new Date(isoString)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}


function normaliseValue(value: unknown, kind: string): unknown {
  if (value === null || value === undefined) {
    if (kind === 'boolean') return false
    if (kind === 'array' || kind === 'array_enum') return []
    return null
  }
  if (kind === 'boolean') return Boolean(value)
  if (kind === 'array' || kind === 'array_enum') {
    if (Array.isArray(value)) return value
    if (typeof value === 'string') {
      try { return JSON.parse(value) } catch { return value ? [value] : [] }
    }
    return []
  }
  if (kind === 'json') {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (Array.isArray(obj)) return JSON.stringify(obj)
    if (typeof obj.name === 'string') return obj.name
    if (typeof obj.label === 'string') return obj.label
    if (typeof obj.id !== 'undefined') return String(obj.id)
    return JSON.stringify(obj)
  }
  return value
}

function normalizeRecord(
  record: Record<string, unknown>,
  resource: string,
  fields: string[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const key of fields) {
    const kind = resolveFieldKind(resource, key)
    result[key] = normaliseValue(record[key], kind)
  }
  return result
}

// ─── Field-level components ───────────────────────────────────────────────────

function BooleanField({
  value,
  onChange,
  label,
}: {
  value: unknown
  onChange: (v: boolean) => void
  label: string
}) {
  const checked = Boolean(value)
  return (
    <label className="flex items-center gap-3 cursor-pointer group select-none shrink-0">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent',
          'transition-colors duration-200 focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-[#076698]/40',
          checked ? 'bg-[#076698]' : 'bg-slate-200 dark:bg-slate-600'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm',
            'transition-transform duration-200',
            checked ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
      <span className="text-sm font-medium leading-none group-hover:text-foreground/80">
        {label}
      </span>
      <span
        className={cn(
          'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
          checked
            ? 'text-[#076698] bg-[#e0f0f7]'
            : 'text-slate-400 bg-slate-100 dark:bg-slate-700/60'
        )}
      >
        {checked ? 'Yes' : 'No'}
      </span>
    </label>
  )
}

function EnumSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const filtered = q ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm text-left',
          'bg-slate-50 dark:bg-slate-700/50',
          'hover:bg-slate-100 dark:hover:bg-slate-700/70 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#076698]/40',
          open
            ? 'border-[#076698]/60 ring-2 ring-[#076698]/20'
            : 'border-input dark:border-slate-600/60'
        )}
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {value || placeholder || 'Select…'}
        </span>
        <svg className="w-3 h-3 text-muted-foreground shrink-0" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-full
                        glass-card rounded-xl border border-white/70 dark:border-white/10
                        shadow-xl overflow-hidden">
          <div className="p-2">
            <input
              autoFocus
              className="w-full px-2 py-1.5 text-sm rounded border bg-background outline-none focus:ring-1 focus:ring-ring"
              placeholder="Search…"
              value={q}
              onChange={e => setQ(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false) }}
              className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
            >
              — clear —
            </button>
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false) }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors',
                  opt === value && 'bg-[#e0f0f7] text-[#076698]'
                )}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function PasswordField({
  value,
  onChange,
  onPasswordChange,
}: {
  value: string
  onChange: (v: string) => void
  onPasswordChange?: (v: string) => void
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          onPasswordChange?.(e.target.value)
        }}
        className="pr-9"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  )
}

// ─── Salt auto-generation ─────────────────────────────────────────────────────

function generateSalt(): string {
  const arr = new Uint8Array(24)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function ResourceForm({ resource, action, defaultValues, recordId }: ResourceFormProps) {
  const {
    control,
    saveButtonProps,
    formState: { isSubmitting, errors },
    refineCore: { query, queryResult, formLoading },
    reset,
    setValue,
    watch,
  } = useForm({
    refineCoreProps: {
      resource,
      action,
      ...(action === 'edit' && recordId ? { id: recordId } : {}),
    },
    ...(action === 'create' && defaultValues ? { defaultValues } : {}),
  })

  const oneResponse = query?.data ?? queryResult?.data
  const record = oneResponse?.data as Record<string, unknown> | undefined
  const loadFailed =
    action === 'edit' && !formLoading &&
    (query?.isError ?? queryResult?.isError) && !record

  // All visible fields in display order
  const fieldsSource = action === 'create' ? defaultValues : record
  const hiddenForResource = [
    ...(HIDDEN_FIELDS[resource] ?? []),
    ...(SALT_FIELDS[resource] ?? []),
  ]

  const fields = fieldsSource
    ? Object.keys(fieldsSource).filter(
        (k) => !['id', 'created_at', 'updated_at'].includes(k) &&
               !hiddenForResource.includes(k)
      )
    : []

  useEffect(() => {
    if (action === 'edit' && record) {
      const normalised = normalizeRecord(record, resource, fields)
      reset(normalised)
    }
  }, [action, record]) // eslint-disable-line react-hooks/exhaustive-deps

  // Dependent FK filters (e.g. role/branch options scoped to selected company)
  const depFilters = FK_DEPENDENT_FILTERS[resource] ?? {}
  const watchedDeps: Record<string, unknown> = {}
  for (const cfg of Object.values(depFilters)) {
    watchedDeps[cfg.dependsOn] = watch(cfg.dependsOn)
  }
  const depKey = Object.values(watchedDeps).map(String).join('|')

  // Clear dependent FK values when a trigger field changes from a real value
  // (skipped on initial load so edit-form resets stay intact)
  const prevDeps = useRef<Record<string, unknown>>({})
  useEffect(() => {
    const prev = prevDeps.current
    for (const [field, cfg] of Object.entries(depFilters)) {
      if (prev[cfg.dependsOn] && prev[cfg.dependsOn] !== watchedDeps[cfg.dependsOn]) {
        setValue(field, '')
      }
    }
    prevDeps.current = { ...watchedDeps }
  }, [depKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Skeleton ──────────────────────────────────────────────────────────────
  if (action === 'edit' && formLoading) {
    return (
      <div className="space-y-5 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3.5 w-24 rounded" />
              <Skeleton className="h-9 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (loadFailed) {
    return (
      <p className="text-sm text-destructive max-w-lg">
        Could not load this record. Check the URL or try again from the list.
      </p>
    )
  }

  const boolFields: string[] = []
  const nonBoolFields: string[] = []

  for (const f of fields) {
    const kind = resolveFieldKind(resource, f)
    if (kind === 'boolean') boolFields.push(f)
    else nonBoolFields.push(f)
  }

  function renderField(field: string) {
    const kind = resolveFieldKind(resource, field)
    const fkMapping = kind !== 'plain_id' ? resolveFkMapping(field) : null
    const watchValue = watch(field)

    const errorMsg = (errors as Record<string, { message?: string }>)[field]?.message

    const labelNode = (
      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
        {toTitleCase(field)}
        {fkMapping && kind !== 'boolean' && kind !== 'hidden' && (
          <Badge
            variant="secondary"
            className="ml-1.5 text-[9px] px-1 py-0 text-[#076698] bg-[#e0f0f7] dark:bg-[#076698]/20 normal-case tracking-normal font-medium"
          >
            → {fkMapping.resource}
          </Badge>
        )}
      </label>
    )

    if (kind === 'hidden') return null

    if (kind === 'boolean') {
      return (
        <div
          key={field}
          className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5
                     bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50"
        >
          <span className="text-sm font-medium text-foreground/80 min-w-0 break-words">{toTitleCase(field)}</span>
          <Controller
            name={field}
            control={control}
            defaultValue={false}
            render={({ field: f }) => (
              <BooleanField
                value={f.value}
                onChange={(v) => f.onChange(v)}
                label=""
              />
            )}
          />
        </div>
      )
    }

    if (kind === 'enum') {
      const raw = ENUM_FIELDS[resource]?.[field] ?? []
      const options = raw.map((o: any) => typeof o === 'string' ? o : o.items).flat()
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <EnumSelect
                value={String(f.value ?? '')}
                onChange={f.onChange}
                options={options}
                placeholder={`Select ${toTitleCase(field)}…`}
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (kind === 'country') {
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <EnumSelect
                value={String(f.value ?? '')}
                onChange={f.onChange}
                options={COUNTRIES}
                placeholder="Select country…"
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (kind === 'array' || kind === 'array_enum') {
      const enumOptions = ARRAY_ENUM_FIELDS[resource]?.[field]
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue={[]}
            render={({ field: f }) => {
              const val = Array.isArray(f.value) ? f.value : []
              return (
                <MultiSelectInput
                  value={val}
                  onChange={f.onChange}
                  options={enumOptions}
                  placeholder={`Add ${toTitleCase(field)}…`}
                />
              )
            }}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (kind === 'password') {
      const saltFields = SALT_FIELDS[resource] ?? []
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <PasswordField
                value={String(f.value ?? '')}
                onChange={f.onChange}
                onPasswordChange={saltFields.length > 0 ? () => {
                  for (const sf of saltFields) {
                    setValue(sf, generateSalt())
                  }
                } : undefined}
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (fkMapping && kind !== 'plain_id') {
      const depCfg = depFilters[field]
      const depVal = depCfg ? watchedDeps[depCfg.dependsOn] : undefined
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <FkSelect
            fieldName={field}
            mapping={fkMapping}
            value={(watchValue as string) ?? null}
            onChange={(id) => setValue(field, id)}
            disabled={!!depCfg && !depVal}
            filter={depCfg && depVal ? { field: depCfg.filterField, value: String(depVal) } : undefined}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
          {depCfg && !depVal && (
            <p className="text-xs text-muted-foreground mt-1">
              Select {toTitleCase(depCfg.dependsOn)} first
            </p>
          )}
        </div>
      )
    }

    if (kind === 'json') {
      return (
        <div key={field} className="space-y-0 sm:col-span-2">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <textarea
                {...f}
                value={f.value === null || f.value === undefined ? '' : String(f.value)}
                rows={8}
                spellCheck={false}
                className={cn(
                  'w-full rounded-lg border bg-slate-50 dark:bg-slate-700/50',
                  'border-input dark:border-slate-600/60 px-3 py-2 text-xs font-mono',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#076698]/40',
                  'focus:border-[#076698]/60 resize-y transition-all'
                )}
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (kind === 'textarea') {
      return (
        <div key={field} className="space-y-0 sm:col-span-2">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <textarea
                {...f}
                value={f.value === null || f.value === undefined ? '' : String(f.value)}
                rows={3}
                className={cn(
                  'w-full rounded-lg border bg-slate-50 dark:bg-slate-700/50',
                  'border-input dark:border-slate-600/60 px-3 py-2 text-sm',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#076698]/40',
                  'focus:border-[#076698]/60 resize-y transition-all'
                )}
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    if (kind === 'datetime') {
      return (
        <div key={field} className="space-y-0">
          {labelNode}
          <Controller
            name={field}
            control={control}
            defaultValue=""
            render={({ field: f }) => (
              <Input
                type="datetime-local"
                value={f.value ? toDatetimeLocalValue(String(f.value)) : ''}
                onChange={(e) => f.onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
                className={cn(errorMsg && 'border-destructive')}
              />
            )}
          />
          {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
        </div>
      )
    }

    return (
      <div key={field} className="space-y-0">
        {labelNode}
        <Controller
          name={field}
          control={control}
          defaultValue=""
          render={({ field: f }) => (
            <Input
              {...f}
              type={kind === 'number' ? 'number' : 'text'}
              value={f.value === null || f.value === undefined ? '' : String(f.value)}
              className={cn(errorMsg && 'border-destructive')}
            />
          )}
        />
        {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
      </div>
    )
  }

  return (
    <form className="space-y-6 max-w-3xl">

      {/* Non-boolean fields in responsive 2-column grid */}
      {nonBoolFields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          {nonBoolFields.map(renderField)}
        </div>
      )}

      {/* Boolean toggle fields in a dedicated card grid */}
      {boolFields.length > 0 && (
        <fieldset className="border border-slate-100 dark:border-slate-700/50 rounded-2xl p-4 space-y-1.5">
          <legend className="px-1 text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
            Flags &amp; Toggles
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {boolFields.map(renderField)}
          </div>
        </fieldset>
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 pt-4 pb-2 border-t border-border bg-background">
        <Button
          type="submit"
          className="bg-[#076698] hover:bg-[#055580] w-full sm:w-auto min-w-28 font-semibold tracking-wide"
          {...saveButtonProps}
          disabled={isSubmitting || saveButtonProps.disabled}
        >
          {isSubmitting ? 'Saving…' : action === 'create' ? 'Create' : 'Save changes'}
        </Button>
      </div>
    </form>
  )
}
